/**
 * The TV's voice.
 *
 * Every cue is synthesised from oscillators at play time rather than loaded
 * from files. That is a deliberate trade: no binary assets to host, nothing to
 * cache-bust, no licensing, and — the reason that matters here — every
 * character of a sound is a number in `audio.tuning.json`, so the tuner can
 * reshape the whole palette live instead of swapping .wav files.
 *
 * Only the TV plays anything. Phones stay silent: a dozen handsets each firing
 * their own buzzer a few milliseconds apart is noise, not feedback.
 */

import audioDefaults from "./audio.tuning.json";
import { audioStore } from "../lib/tuning.js";

export type AudioTuning = typeof audioDefaults;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let filter: BiquadFilterNode | null = null;

/** Browsers refuse to start an AudioContext until the page has been touched. */
export function audioReady(): boolean {
  return ctx !== null && ctx.state === "running";
}

/**
 * Create or resume the context. Must be called from inside a user gesture the
 * first time; after that it is cheap and idempotent.
 */
export function unlockAudio(): void {
  if (typeof window === "undefined") return;
  if (!ctx) {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;
    ctx = new Ctor();
    filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    master = ctx.createGain();
    filter.connect(master);
    master.connect(ctx.destination);
  }
  if (ctx.state === "suspended") void ctx.resume();
}

function cfg(): AudioTuning {
  return audioStore.get();
}

/**
 * One shaped note. Everything else in this file is a sequence of these.
 *
 * Two oscillators detuned against each other rather than one: a single
 * oscillator reads as a test tone, a pair beating slightly reads as an
 * instrument. The gain envelope is scheduled rather than tweened so a cue is
 * sample-accurate even when the main thread is busy animating a reveal.
 */
function note(
  at: number,
  hz: number,
  durationMs: number,
  gain: number,
  opts: { waveform?: OscillatorType; bendTo?: number } = {},
): void {
  if (!ctx || !filter) return;
  const c = cfg();
  const dur = Math.max(0.01, durationMs / 1000);
  const attack = Math.max(0.001, c.master.attack_ms / 1000);
  const release = Math.max(0.01, c.master.release_ms / 1000);

  const env = ctx.createGain();
  env.gain.setValueAtTime(0, at);
  env.gain.linearRampToValueAtTime(gain, at + attack);
  env.gain.setValueAtTime(gain, at + dur);
  // Exponential tails sound natural; linear ones click at the end.
  env.gain.exponentialRampToValueAtTime(0.0001, at + dur + release);
  env.connect(filter);

  for (const detune of [-c.master.detune_cents, c.master.detune_cents]) {
    const osc = ctx.createOscillator();
    osc.type = opts.waveform ?? (c.master.waveform as OscillatorType);
    osc.frequency.setValueAtTime(hz, at);
    if (opts.bendTo !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(20, opts.bendTo),
        at + dur,
      );
    }
    osc.detune.setValueAtTime(detune, at);
    osc.connect(env);
    osc.start(at);
    osc.stop(at + dur + release + 0.02);
  }
}

/** Filtered noise burst — the percussive half of the palette. */
function thud(at: number, durationMs: number, gain: number, hz: number): void {
  if (!ctx || !filter) return;
  const dur = Math.max(0.02, durationMs / 1000);
  const frames = Math.ceil(ctx.sampleRate * dur);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) {
    // Decaying white noise. The square of the ramp falls faster than the ear
    // expects, which is what makes it read as a hit rather than a hiss.
    const fall = 1 - i / frames;
    data[i] = (Math.random() * 2 - 1) * fall * fall;
  }
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  const band = ctx.createBiquadFilter();
  band.type = "bandpass";
  band.frequency.setValueAtTime(hz, at);
  band.Q.setValueAtTime(1.2, at);
  const env = ctx.createGain();
  env.gain.setValueAtTime(gain, at);
  env.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  src.connect(band);
  band.connect(env);
  env.connect(filter);
  src.start(at);
  src.stop(at + dur);
}

const semi = (hz: number, steps: number) => hz * Math.pow(2, steps / 12);

export type Cue =
  | "correct"
  | "wrong"
  | "duplicate"
  | "nearMiss"
  | "timeout"
  | "pass"
  | "reveal"
  | "categoryIn"
  | "clearBoard"
  | "matchEnd"
  | "tick"
  | "join";

/**
 * Fire a cue.
 *
 * `index` colours cues that repeat within one beat: the rank of the answer just
 * found, or the position of a slot in the closing cascade. It walks the cue up
 * a scale so eight reveals in a row are a run rather than the same blip eight
 * times.
 */
export function playCue(cue: Cue, index = 0): void {
  const c = cfg();
  if (!c.master.enabled) return;
  unlockAudio();
  if (!ctx || !master || !filter) return;

  master.gain.setTargetAtTime(c.master.volume, ctx.currentTime, 0.01);
  filter.frequency.setTargetAtTime(c.master.lowpass_hz, ctx.currentTime, 0.01);

  const t = ctx.currentTime + 0.008;

  switch (cue) {
    case "correct": {
      const s = c.correct;
      // Higher-ranked answers ring higher, so the room hears how good the find
      // was before the score even moves.
      const lift =
        index > 0 ? ((8 - Math.min(index, 8)) / 7) * s.rankLift_semitones : 0;
      const root = semi(s.root_hz, lift);
      note(t, root, s.duration_ms, s.volume);
      note(
        t + s.noteGap_ms / 1000,
        semi(root, s.interval_semitones),
        s.duration_ms,
        s.volume * 0.9,
      );
      break;
    }
    case "wrong": {
      const s = c.wrong;
      note(t, s.root_hz, s.duration_ms, s.volume, { waveform: "sawtooth" });
      // A second oscillator a fraction of a semitone off beats against the
      // first — that slow throb is what makes a buzzer sound sour.
      note(t, semi(s.root_hz, s.detune_semitones), s.duration_ms, s.volume, {
        waveform: "sawtooth",
      });
      thud(t, 120, s.volume * 0.5, 180);
      break;
    }
    case "duplicate": {
      const s = c.duplicate;
      note(t, s.root_hz, s.duration_ms, s.volume);
      thud(t, 90, s.volume * 0.4, 400);
      break;
    }
    case "nearMiss": {
      const s = c.nearMiss;
      note(t, s.root_hz, s.duration_ms, s.volume, {
        bendTo: semi(s.root_hz, s.bend_semitones),
      });
      break;
    }
    case "timeout": {
      const s = c.timeout;
      note(t, s.root_hz, s.duration_ms, s.volume, {
        bendTo: semi(s.root_hz, -s.fall_semitones),
      });
      break;
    }
    case "pass": {
      const s = c.pass;
      // Flat, short, unresolved: passing should sound like nothing happened.
      note(t, s.root_hz, s.duration_ms, s.volume);
      break;
    }
    case "reveal": {
      const s = c.reveal;
      note(
        t,
        semi(s.root_hz, index * s.step_semitones),
        s.duration_ms,
        s.volume,
      );
      break;
    }
    case "categoryIn": {
      const s = c.categoryIn;
      note(t, s.root_hz, s.duration_ms, s.volume, {
        bendTo: semi(s.root_hz, s.rise_semitones),
      });
      break;
    }
    case "clearBoard": {
      const s = c.clearBoard;
      // Major arpeggio plus the octave — the only unambiguously triumphant
      // shape in the palette, saved for the one event that earns it.
      [0, 4, 7, 12].forEach((step, i) =>
        note(
          t + (i * s.noteGap_ms) / 1000,
          semi(s.root_hz, step),
          s.duration_ms,
          s.volume,
        ),
      );
      break;
    }
    case "matchEnd": {
      const s = c.matchEnd;
      [0, 7, 12, 16, 19].forEach((step, i) =>
        note(
          t + (i * s.noteGap_ms) / 1000,
          semi(s.root_hz, step),
          s.duration_ms,
          s.volume,
        ),
      );
      break;
    }
    case "tick": {
      const s = c.tick;
      note(t, s.root_hz, s.duration_ms, s.volume, { waveform: "square" });
      break;
    }
    case "join": {
      const s = c.join;
      note(t, s.root_hz, s.duration_ms, s.volume);
      break;
    }
  }
}

/** Audition a cue from the tuner without a game running. */
export function previewCue(cue: Cue): void {
  unlockAudio();
  playCue(cue, cue === "reveal" ? 3 : cue === "correct" ? 1 : 0);
}
